import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { ProposalActivitySource } from '@/services/ResearchService';

export function ProposalActivitySourcePicker({
  city,
  value,
  options,
  onChange,
}: {
  city: string;
  value?: string;
  options: ProposalActivitySource[];
  onChange: (id: string) => Promise<void>;
}) {
  const [selected, setSelected] = useState(value || '');
  const [confirm, setConfirm] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState('');
  useEffect(() => setSelected(value || ''), [value]);
  const current = options.find((o) => o.id === value);
  async function apply() {
    setSaving(true);
    setError('');
    try {
      await onChange(selected);
      setConfirm(false);
    } catch {
      setError('The activity list could not be changed. Please try again.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50 p-4 space-y-2 text-gray-900">
      <p className="text-sm font-semibold">Client activity list for {city}</p>
      <p className="text-xs text-gray-600">
        {current
          ? `Using ${current.city}'s saved list (${current.active_count} active).`
          : 'Choose a saved list before adding activities.'}{' '}
        The client sees only this list. Removing every activity leaves the section empty.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-0 text-xs font-medium">
          Saved destination lists
          <select
            aria-label="Saved destination activity list"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={saving}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white p-2 text-sm"
          >
            {!value && <option value="">Choose a list…</option>}
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.city} — {o.active_count} active
                {o.total_count > o.active_count
                  ? `, ${o.total_count - o.active_count} excluded`
                  : ''}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          size="sm"
          disabled={!selected || selected === value || saving}
          onClick={() => setConfirm(true)}
        >
          Use selected list
        </Button>
      </div>
      <p className="text-xs text-gray-600">
        Other saved lists are kept, not combined. To edit another list, select it and confirm the
        change.
      </p>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <ConfirmDialog
        isOpen={confirm}
        onClose={() => {
          if (!saving) setConfirm(false);
        }}
        onConfirm={() => void apply()}
        title="Change the client activity list?"
        description={`The proposal will use ${options.find((o) => o.id === selected)?.city || 'this destination'}'s activities instead. Existing lists will not be deleted.`}
        confirmText="Use this list"
        isLoading={saving}
      />
    </div>
  );
}
